import React, { useCallback, useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Device } from '@twilio/voice-sdk'
import { updateCallInformation, updateMuteStatus, refreshToken } from '../../actions'

const TwilioVoiceContext = React.createContext(null)
export { TwilioVoiceContext }

const TwilioVoiceManager = ({ children }) => {
    const voiceDevice = useRef(null)
    const [isRegistered, setIsRegistered] = useState(false)
    const twilioAccessToken = useSelector(state => state.twilioAccessToken)
    const numberInUse = useSelector(state => state.numberInUse ? state.numberInUse.phoneNumber : "")
    const dispatch = useDispatch()
    const [activeCall, setActiveCall] = useState(null)

    const updateCallInfo = useCallback((call) => {
        dispatch(updateCallInformation(call))
    }, [dispatch])

    const updateIsMutedStatus = useCallback((isMuted) => {
        dispatch(updateMuteStatus(isMuted))
    }, [dispatch])

    const makeCall = async (destination) => {
        try {
            const call = await voiceDevice.current.connect({
                params: {
                    "to": destination,
                    "from": numberInUse,
                    "identity": "dev-phone"
                }
            })
            setActiveCall(call)
        } catch (error) {
            console.error("Failed to make call:", error)
        }
    }

    // Handle call events when an active call exists
    useEffect(() => {
        if (!activeCall) return

        updateCallInfo(activeCall)

        activeCall.on('accept', call => {
            updateCallInfo(call)
        })

        activeCall.on('connect', call => {
            updateCallInfo(call)
        })

        activeCall.on('disconnect', call => {
            call.removeAllListeners()
            updateCallInfo(null)
            setActiveCall(null)
        })

        activeCall.on('mute', isMuted => {
            updateIsMutedStatus(isMuted)
        })
    }, [activeCall, updateCallInfo, updateIsMutedStatus])

    // Initialize Twilio Voice Device
    useEffect(() => {
        if (!twilioAccessToken || voiceDevice.current) return

        const device = new Device(twilioAccessToken, {
            codecPreferences: ["opus", "pcmu"],
            fakeLocalDTMF: true,
            debug: false,
            enableRingingState: true
        })

        let destroyed = false

        navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
                stream.getTracks().forEach(track => track.stop())
            })
            .catch(() => {})
            .finally(() => {
                if (!destroyed) device.register()
            })

        device.on("registered", () => { setIsRegistered(true) })

        device.on("incoming", (call) => {
            setActiveCall(call)
        })

        device.on("tokenWillExpire", async () => {
            try {
                const newToken = await refreshToken(dispatch)
                device.updateToken(newToken)
            } catch (error) {
                console.error("Failed to refresh token:", error)
            }
        })

        device.on("error", async (error) => {
            if (error.code === 20104) {
                try {
                    const newToken = await refreshToken(dispatch)
                    device.updateToken(newToken)
                    if (!destroyed) device.register()
                } catch (refreshError) {
                    console.error("Failed to refresh expired token:", refreshError)
                }
            } else {
                console.error("Voice device error:", error)
            }
        })

        voiceDevice.current = device

        return () => {
            destroyed = true
            device.destroy()
            voiceDevice.current = null
            setIsRegistered(false)
        }
    }, [twilioAccessToken, dispatch])

    const contextValue = {
        voiceDevice: isRegistered ? voiceDevice : null,
        hangUp: () => { },
        sendDTMF: () => { },
        updateCallInfo,
        makeCall,
        toggleMute: () => { }
    }

    // Update call-specific functions when there's an active call
    if (activeCall) {
        contextValue.acceptCall = () => activeCall.accept()
        contextValue.hangUp = () => {
            activeCall.disconnect()
            setActiveCall(null)
        }
        contextValue.sendDTMF = (num) => activeCall.sendDigits(num)
        contextValue.toggleMute = () => {
            activeCall.mute(!activeCall.isMuted())
        }
    }

    return (
        <TwilioVoiceContext.Provider value={contextValue}>
            {children}
        </TwilioVoiceContext.Provider>
    )
}

export default TwilioVoiceManager
